// @ts-check

import React from "react";
import { Alert, Button, Checkbox, Select } from "antd";
import { PlusOutlined } from "@ant-design/icons";

import { RowInfos } from "../row-infos";

/**
 * @typedef {Object} Props
 *
 * @prop {import("../import-state/import-state").ImportStateService} service
 * @prop {import("../domain").ImportState} state
 *
 * @prop {() => void} onBack
 * @prop {(() => void) | null} onNext
 */

/**
 * @param {Props} props
 */
export const ConfigureEntityMatch = (props) => {
  const { onNext, onBack, service, state } =
    props;

  const {
    config,
    dataIndexOptions,
    requiredFields,
    canCreateIfNoMatch,
  } = state.entityMatch;

  /**
   * @type {JSX.Element[]}
   */
  const successes = [];

  const counts = { create: 0, update: 0, couldCreate: 0, couldUpdate: 0 };
  for (const row of state.reducedRows) {
    if (row.previous) {
      counts.couldUpdate = counts.couldUpdate + 1;

      if (row.isValid) {
        counts.update = counts.update + 1;
      }
    } else {
      counts.couldCreate = counts.couldCreate + 1;

      if (row.isValid) {
        counts.create = counts.create + 1;
      }
    }
  }

  if (counts.create) {
    successes.push(
      <React.Fragment key="create_success">
        create <strong>{counts.create}</strong> new
      </React.Fragment>
    );
  }

  if (counts.update) {
    successes.push(
      <React.Fragment key="update_success">
        update <strong>{counts.update}</strong> existing
      </React.Fragment>
    );
  }

  if (successes.length === 2) {
    successes.splice(1, 0, <> and </>);
  }

  /**
   * @type {JSX.Element[]}
   */
  const issues = [];

  // TODO: test this case
  if (counts.couldCreate && !canCreateIfNoMatch) {
    issues.push(
      <div className="mb-2">
        Cannot create entities for <strong>{counts.couldCreate}</strong>{" "}
        unmatched rows as field{requiredFields.length > 2 ? "s" : ""}:
        <br />
        <strong>{requiredFields.map(({ name }) => name).join()}</strong>
        <br />
        {requiredFields.length > 2 ? "are" : "is"} required.
      </div>
    );
  }

  // TODO:
  // - confirm that the checkbox still works for create... or don't
  // allow that checkbox, it seems dumb.

  return (
    <div className="w-full">
      <>
        <div className="mb-2 text-lg">
          Match existing records by
        </div>
        {config.dataIndexes.map((dataIndexes, rowIndex) => {
          const unusedOptions = dataIndexOptions.filter((option) => {
            return !dataIndexes.includes(option.value);
          });

          return (
            <React.Fragment key={rowIndex}>
              <div className="mb-2 flex items-center">
                {dataIndexes.map((dataIndex, columnIndex) => {
                  return (
                    <React.Fragment key={dataIndex}>
                      <Select
                        allowClear={dataIndexes.length > 1 || service.canRemoveEntityMatchKey()}
                        className="flex-1"
                        onClear={() => {
                          const next = [...dataIndexes];
                          next.splice(columnIndex, 1);

                          if (!next.length) {
                            service.removeEntityMatchKey({
                              index: rowIndex,
                            });
                            return
                          }

                          service.setEntityMatchKey({
                            value: next,
                            index: rowIndex,
                          });
                        }}
                        onSelect={(value) => {
                          const next = [...dataIndexes];
                          next.splice(columnIndex, 1, value);

                          service.setEntityMatchKey({
                            value: next,
                            index: rowIndex
                          });
                        }}
                        options={dataIndexOptions}
                        value={dataIndex}
                      />
                      {columnIndex + 1 < dataIndexes.length ? (
                        <div className="mx-2 font-bold">And</div>
                      ) : null}
                    </React.Fragment>
                  )
                })}
                {unusedOptions.length && dataIndexes.length < 3 ? (
                  <Button
                    rootClassName="ml-2"
                    size="small"
                    icon={<PlusOutlined />}
                    iconPosition="end"
                    onClick={() => {
                      service.setEntityMatchKey({
                        value: [...dataIndexes, unusedOptions[0].value],
                        index: rowIndex,
                      })
                    }}
                  >
                    And
                  </Button>
                ) : null}
              </div>
              {rowIndex + 1 < config.dataIndexes.length ? (
                <div className="my-1 font-bold">Or</div>
              ) : null}
            </React.Fragment>
          )
        })}
      </>
      {service.canAddEntityMatchKey() && (
        <div className="flex justify-start mb-2 items-center">
          <Button
            onClick={() => service.addEntityMatchKey()}
            icon={<PlusOutlined />}
            iconPosition="end"
            size="small"
          >OR</Button>
        </div>
      )}

      {canCreateIfNoMatch && counts.couldCreate ? (
        <div className="mb-2 flex items-start">
          <Checkbox
            onChange={(e) => {
              service.setCreateEntityIfNoMatch({
                value: e.target.checked
              });
            }}
            checked={config.createIfNoMatch}
            style={{ marginRight: "8px" }}
          />
          {counts.couldCreate > 1 ? (
            <div>
              Create new records for the rows that do not match?
            </div>
          ) : (

            <div>
              Create new record for the{" "}
              <strong>{counts.couldCreate}</strong> row that does not match?
            </div>
          )}
        </div>
      ) : null}


      <RowInfos
        reducedRows={state.reducedRows}
        filter={(info) => {
          if (info.level === "info") {
            return false;
          }

          if (info.type !== "entity_match_info") {
            return false;
          }

          return true;
        }}
      />

      {successes.length ? (
        <Alert
          type="success"
          message={<div>Import will {successes} records.</div>}
          className="!my-2"
        />
      ) : (
        <Alert
          type="warning"
          message={
            <div>This import will not update or create any new records.</div>
          }
          className="!my-2"
        />
      )}

      <div className="flex justify-end">
        <Button className="mr-2" onClick={onBack}>Back</Button>
        <Button type="primary" disabled={!onNext} onClick={onNext ?? undefined}
        >Next</Button>
      </div>
    </div>
  );
};
